import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useRole } from "../lib/roleContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { ChevronLeft, Check, Lock, ShieldCheck, FileCheck2, UserRound, X, Wallet } from "lucide-react";
import { relDate } from "../lib/format";
import { KatlasRecord, KatlasModeChip, PolicyGatePassedChip, ReceiptSignedChip } from "../components/Katlas";

const STATUS_LABEL = {
  pending_parent: "Awaiting parent/guardian approval",
  pending_officer: "Awaiting club officer approval",
  pending_officer_and_parent: "Awaiting officer + parent approval",
  claim_recorded: "Claim recorded",
  declined: "Declined",
};

export default function OfferReceipt() {
  const { id } = useParams();
  const { role } = useRole();
  const [c, setC] = useState(null);
  const [notes, setNotes] = useState("");

  const refresh = () => api.getClaim(id).then(setC);
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [id]);

  if (!c) return <Card className="p-8 text-sm text-muted-foreground">Loading claim…</Card>;

  const isParent = ["parent_guardian"].includes(role.id);
  const isOfficer = ["club_secretary", "committee"].includes(role.id);
  const canParent = c.evaluation.needs_parent_approval && !c.parent_decision && isParent && c.status !== "declined";
  const canOfficer = c.evaluation.needs_officer_approval && !c.officer_decision && isOfficer && c.status !== "declined";

  const run = async (fn, label) => {
    try {
      await fn();
      toast.success(label);
      setNotes("");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="offer-receipt-page">
      <Link to={`/drivers/${c.driver_id}/offers`} className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-offers">
        <ChevronLeft className="h-4 w-4" /> Back to offers
      </Link>

      <Card className="p-6 sm:p-8 border-border race-card space-y-5" data-testid="receipt-card">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="tracking-eyebrow text-muted-foreground">Shop / service receipt</div>
            <h1 className="text-2xl sm:text-3xl font-semibold mt-1">{c.offer_snapshot.name}</h1>
            <div className="text-[12px] text-muted-foreground font-mono-ui mt-1">
              {c.id} · hash {c.hash} · {relDate(c.created_at)}
            </div>
          </div>
          <span className="text-[11px] tracking-eyebrow font-mono-ui px-2 py-1 rounded-full border border-border bg-white" data-testid="receipt-status">
            {STATUS_LABEL[c.status] || c.status}
          </span>
        </div>

        <div className="flex flex-wrap gap-1.5">
          <KatlasModeChip />
          <PolicyGatePassedChip passed={c.status === "claim_recorded" || c.status.startsWith("pending")} />
          <ReceiptSignedChip />
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <div className="space-y-3">
            <Row label="Member" value={`${c.driver_snapshot.name} · ${c.driver_snapshot.age_band}`} />
            <Row label="Member role" value="Driver" />
            <Row label="Personalised" value={c.personalised ? "Yes" : "No"} />
            <Row label="Supplier" value={`${c.offer_snapshot.supplier}${c.offer_snapshot.supplier_verified ? " (verified)" : ""}`} />
            <Row label="Policy gate result" value={c.evaluation.state.replace(/_/g, " ")} mono />
          </div>
          <div className="space-y-3">
            <div>
              <div className="tracking-eyebrow text-muted-foreground">Wallet proofs used</div>
              <ul className="mt-1.5 text-sm space-y-1">
                {(c.wallet_proofs_used || []).map((w) => (
                  <li key={w} className="flex gap-2"><Wallet className="h-3.5 w-3.5 mt-0.5 text-[#4A5D4E]" /><span>{w}</span></li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="tracking-eyebrow text-muted-foreground">Shared</div>
                <ul className="mt-1 text-xs space-y-0.5">
                  {(c.shared_fields || []).map((s) => <li key={s} className="flex gap-1.5"><Check className="h-3 w-3 mt-0.5 text-[var(--rag-green-text)]" /><span>{s}</span></li>)}
                  {(c.shared_fields || []).length === 0 && <li className="italic text-muted-foreground">No personal data shared</li>}
                </ul>
              </div>
              <div>
                <div className="tracking-eyebrow text-muted-foreground">Withheld</div>
                <ul className="mt-1 text-xs space-y-0.5">
                  {(c.withheld_fields || []).map((s) => <li key={s} className="flex gap-1.5"><Lock className="h-3 w-3 mt-0.5 text-muted-foreground" /><span>{s}</span></li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Approval chain */}
        <div className="border-t border-border pt-5 space-y-3">
          <div className="tracking-eyebrow text-muted-foreground">Approval chain</div>
          <ApprovalRow
            icon={<UserRound className="h-4 w-4" />}
            label="Parent / guardian"
            required={c.evaluation.needs_parent_approval}
            decision={c.parent_decision}
          />
          <ApprovalRow
            icon={<ShieldCheck className="h-4 w-4" />}
            label="Club officer"
            required={c.evaluation.needs_officer_approval}
            decision={c.officer_decision}
          />
        </div>

        {(canParent || canOfficer) && c.status !== "claim_recorded" && (
          <div className="border-t border-border pt-5 space-y-3 bg-[#FAFAFB] -mx-6 -mb-6 px-6 pb-6 sm:-mx-8 sm:px-8" data-testid="action-panel">
            <div className="text-sm">
              You are reviewing as <b>{role.name}</b> ({role.label}).
            </div>
            <Textarea
              placeholder="Optional notes for the receipt…"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="decision-notes"
            />
            <div className="flex flex-wrap gap-2">
              {canParent && (
                <Button onClick={() => run(() => api.parentApproveClaim(c.id, { reviewer_name: role.name, reviewer_role: role.label, notes }), "Parent approval recorded")} data-testid="parent-approve-btn">
                  Approve as parent
                </Button>
              )}
              {canOfficer && (
                <Button onClick={() => run(() => api.officerApproveClaim(c.id, { reviewer_name: role.name, reviewer_role: role.label, notes }), "Officer approval recorded")} data-testid="officer-approve-btn">
                  Approve as officer
                </Button>
              )}
              <Button variant="outline" onClick={() => run(() => api.declineClaim(c.id, { reviewer_name: role.name, reviewer_role: role.label, notes }), "Claim declined")} data-testid="decline-btn">
                <X className="h-3.5 w-3.5 mr-1" /> Decline
              </Button>
            </div>
            <div className="text-[11px] text-muted-foreground">
              {c.evaluation.needs_parent_approval && !c.parent_decision && !isParent && (
                <span>To record the parent/guardian decision, switch the role toggle to <b>Parent / Guardian</b>.</span>
              )}
            </div>
          </div>
        )}

        {c.status === "claim_recorded" && (
          <div className="border-t border-border pt-4 text-sm text-[var(--rag-green-text)] flex items-center gap-2">
            <FileCheck2 className="h-4 w-4" /> Claim recorded. This receipt is now part of the evidence trail.
          </div>
        )}

        <KatlasRecord
          hash={c.hash}
          receiptId={c.id}
          policyGate={c.status === "declined" ? "blocked" : "passed"}
          actor={
            c.officer_decision
              ? `${c.officer_decision.decided_by} (${c.officer_decision.decided_role})`
              : c.parent_decision
              ? `${c.parent_decision.decided_by} (${c.parent_decision.decided_role})`
              : "Pending approval"
          }
          action={
            c.status === "claim_recorded"
              ? "Offer claim recorded"
              : c.status === "declined"
              ? "Offer claim declined"
              : "Offer claim raised — awaiting approval"
          }
          shared={c.shared_fields}
          withheld={c.withheld_fields}
          attestation={c.katlas_attestation || null}
          timestamp={c.recorded_at || c.created_at || null}
          car={{
            custody: `${c.driver_snapshot?.name || "Member"}'s wallet controls age band, role and the shared fields. Parent / guardian wallet controls junior-specific approvals.`,
            authority: c.officer_decision
              ? `${c.officer_decision.decided_role} approved the claim under policy gate CAR-CLUBCIERGE-001.`
              : c.parent_decision
              ? `${c.parent_decision.decided_role} approved the claim under policy gate CAR-CLUBCIERGE-001.`
              : "Awaiting approval from the authorised role(s) under policy gate CAR-CLUBCIERGE-001.",
            receipt: c.katlas_attestation?.mode === "live"
              ? `KATLAS signed the claim at ${c.katlas_attestation.node_timestamp || c.recorded_at}, producing receipt hash ${c.katlas_attestation.attestation_hash || c.hash}.`
              : c.status === "claim_recorded"
              ? `KATLAS governance simulation produced receipt hash ${c.hash} at ${c.recorded_at}. A live node would attest the same action.`
              : `Receipt will be signed once required approvals are in place.`,
          }}
        />

        <div className="text-[11px] text-muted-foreground border-t border-border pt-3 italic leading-relaxed">
          This receipt records the eligibility check and approval path. It does not publish private junior data,
          withheld assets or payment details. KATLAS governs the permissioned claim — it does not process
          payment.
        </div>
      </Card>
    </div>
  );
}

function Row({ label, value, mono = false }) {
  return (
    <div>
      <div className="tracking-eyebrow text-muted-foreground">{label}</div>
      <div className={`text-sm mt-0.5 ${mono ? "font-mono-ui" : ""}`}>{value}</div>
    </div>
  );
}

function ApprovalRow({ icon, label, required, decision }) {
  if (!required) {
    return (
      <div className="flex items-center gap-3 text-sm text-muted-foreground">
        <span className="text-muted-foreground">{icon}</span>
        <span>{label}: not required for this offer</span>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-3 text-sm" data-testid={`approval-row-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <span className="text-[#4A5D4E] mt-0.5">{icon}</span>
      <div className="flex-1">
        <div><b>{label}</b> approval required</div>
        {decision ? (
          <div className="text-[12px] text-muted-foreground mt-0.5">
            {decision.outcome === "approved" ? "Approved" : "Declined"} by {decision.decided_by} ({decision.decided_role}) · {relDate(decision.decided_at)}
            {decision.notes && <div className="italic">“{decision.notes}”</div>}
          </div>
        ) : (
          <div className="text-[12px] text-muted-foreground mt-0.5">Pending</div>
        )}
      </div>
    </div>
  );
}
